<template>
  <div class="error-page">
    <div class="error-page__container">
      <div class="error-page__content">
        <!-- Иконка ошибки -->
        <div class="error-page__icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>

        <!-- Код ошибки -->
        <h1 class="error-page__code">{{ errorCode }}</h1>

        <!-- Заголовок -->
        <h2 class="error-page__title">{{ errorTitle }}</h2>

        <!-- Описание -->
        <p class="error-page__description">
          {{ errorMessage }}
        </p>

        <!-- Кнопки -->
        <div class="error-page__actions">
          <UButton 
            label="На главную" 
            size="lg"
            icon="i-heroicons-home"
            @click="goHome"
          />
          <UButton 
            v-if="showBackButton"
            label="Назад" 
            size="lg"
            color="gray"
            variant="outline"
            icon="i-heroicons-arrow-left"
            @click="goBack"
          />
        </div>
      </div>
    </div>
    <ClientOnly>
      <UNotifications />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { consola } from 'consola'
import type { NuxtError } from '#app'

// Props
const props = defineProps<{
  error: NuxtError
}>()

// Logger
const logger = consola.withTag('error-page')

const router = useRouter()

// Вычисляем код ошибки
const errorCode = computed(() => {
  return props.error?.statusCode || 500
})

// Вычисляем заголовок ошибки
const errorTitle = computed(() => {
  switch (errorCode.value) {
    case 404: {
      return 'Страница не найдена'
    }
    case 403: {
      return 'Доступ запрещен'
    }
    case 500: {
      return 'Внутренняя ошибка сервера'
    }
    default: {
      return 'Произошла ошибка'
    }
  }
})

// Вычисляем сообщение ошибки
const errorMessage = computed(() => {
  if (props.error?.message) {
    return props.error.message
  }
  
  switch (errorCode.value) {
    case 404: {
      return 'К сожалению, запрашиваемая страница не существует или была удалена.'
    }
    case 403: {
      return 'У вас нет прав доступа к этой странице. Пожалуйста, авторизуйтесь.'
    }
    case 500: {
      return 'На сервере произошла ошибка. Пожалуйста, попробуйте позже.'
    }
    default: {
      return 'Что-то пошло не так. Пожалуйста, попробуйте обновить страницу.'
    }
  }
})

// Показывать ли кнопку "Назад"
const showBackButton = computed(() => {
  return router.options.history.state.back !== null
})

// Логирование ошибки
onMounted(() => {
  logger.error('Ошибка приложения:', {
    statusCode: errorCode.value,
    message: props.error?.message,
    statusMessage: props.error?.statusMessage,
    stack: props.error?.stack,
  })
})

/**
 * Переход на главную страницу
 */
const goHome = () => {
  logger.debug('Редирект на главную страницу')
  clearError({ redirect: '/' })
}

/**
 * Возврат на предыдущую страницу
 */
const goBack = () => {
  logger.debug('Возврат на предыдущую страницу')
  router.back()
}
</script>

<style lang="scss" scoped>
.error-page {
  @apply min-h-screen flex items-center justify-center px-4;
  @apply bg-gradient-to-br from-gray-50 to-gray-100;
  @apply dark:from-gray-900 dark:to-gray-800;

  &__container {
    @apply w-full max-w-2xl;
  }

  &__content {
    @apply flex flex-col items-center text-center;
    @apply bg-white dark:bg-gray-800/50;
    @apply rounded-2xl shadow-xl;
    @apply p-8 md:p-12;
    @apply border border-gray-200 dark:border-gray-700;
  }

  &__icon {
    @apply w-24 h-24 mb-6;
    @apply text-red-500 dark:text-red-400;
    
    svg {
      @apply w-full h-full;
      animation: pulse 2s ease-in-out infinite;
    }
  }

  &__code {
    @apply text-8xl md:text-9xl font-bold mb-4;
    @apply bg-gradient-to-r from-red-500 to-orange-500;
    @apply bg-clip-text text-transparent;
  }

  &__title {
    @apply text-2xl md:text-3xl font-bold mb-4;
    @apply text-gray-900 dark:text-gray-100;
  }

  &__description {
    @apply text-base md:text-lg mb-8;
    @apply text-gray-600 dark:text-gray-400;
    @apply max-w-md;
  }

  &__actions {
    @apply flex flex-col sm:flex-row gap-4;
    @apply w-full sm:w-auto;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
